import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { X, CheckCircle, XCircle, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyQuestionPopup({ onClose }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [question, setQuestion] = useState(null);
  const [translatedQuestion, setTranslatedQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [translatedResult, setTranslatedResult] = useState(null);
  const [showQuestion, setShowQuestion] = useState(false);

  // Function to translate text using the backend API
  const translateText = useCallback(async (texts, targetLanguage) => {
    if (targetLanguage === 'en') return texts;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/translate-quiz`,
        {
          quiz_data: {
            questions: [{
              question: texts.question,
              options: [texts.option_1, texts.option_2],
              explanation: texts.explanation || ''
            }]
          },
          target_language: targetLanguage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.translated_quiz?.questions?.[0]) {
        const translated = response.data.translated_quiz.questions[0];
        return {
          question: translated.question,
          option_1: translated.options?.[0] || texts.option_1,
          option_2: translated.options?.[1] || texts.option_2,
          explanation: translated.explanation || texts.explanation
        };
      }
      return texts;
    } catch (error) {
      console.error('Translation failed:', error);
      return texts;
    }
  }, []);

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        const response = await axios.get(`${API_URL}/daily-question`);
        if (response.data.show_question && response.data.question) {
          const fetchedQuestion = response.data.question;
          setQuestion(fetchedQuestion);
          setShowQuestion(true);
          
          // Translate if language is not English
          if (i18n.language !== 'en') {
            setTranslating(true);
            const translated = await translateText({
              question: fetchedQuestion.question,
              option_1: fetchedQuestion.option_1,
              option_2: fetchedQuestion.option_2,
              explanation: fetchedQuestion.explanation
            }, i18n.language);
            setTranslatedQuestion(translated);
            setTranslating(false);
          }
        } else {
          onClose?.();
        }
      } catch (error) {
        console.error('Failed to fetch daily question:', error);
        onClose?.();
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [onClose, i18n.language, translateText]);

  const handleSubmit = async () => {
    if (!selectedAnswer) {
      toast.error(t('dailyQuestion.selectAnswer'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/daily-question/answer`, {
        question_id: question.id,
        selected_answer: selectedAnswer
      });
      setResult(response.data);
      
      // Translate result explanation if not English
      if (i18n.language !== 'en' && response.data.explanation) {
        const translatedExp = await translateText({
          question: '',
          option_1: '',
          option_2: '',
          explanation: response.data.explanation
        }, i18n.language);
        setTranslatedResult({
          ...response.data,
          explanation: translatedExp.explanation,
          message: response.data.is_correct ? t('dailyQuestion.correct') : t('dailyQuestion.incorrect')
        });
      } else {
        setTranslatedResult(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dailyQuestion.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  // Get the display text (translated or original)
  const displayQuestion = translatedQuestion || question;
  const displayResult = translatedResult || result;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-background rounded-2xl p-6 flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!showQuestion || !question) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="daily-question-popup"
    >
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              <span className="font-semibold">{t('dailyQuestion.title')}</span>
            </div>
            {result && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {!result ? (
            <>
              {/* Question */}
              <div className="mb-6">
                {translating ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('quiz.translating')}</span>
                  </div>
                ) : (
                  <p className="text-lg font-medium text-foreground leading-relaxed">
                    {displayQuestion?.question || question.question}
                  </p>
                )}
              </div>

              {/* Options */}
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => setSelectedAnswer(1)}
                  disabled={submitting || translating}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                    selectedAnswer === 1
                      ? 'border-primary bg-primary/10 shadow-md'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  data-testid="option-1"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      selectedAnswer === 1
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      A
                    </div>
                    <span className="flex-1 text-sm md:text-base">
                      {displayQuestion?.option_1 || question.option_1}
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedAnswer(2)}
                  disabled={submitting || translating}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                    selectedAnswer === 2
                      ? 'border-primary bg-primary/10 shadow-md'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  data-testid="option-2"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      selectedAnswer === 2
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      B
                    </div>
                    <span className="flex-1 text-sm md:text-base">
                      {displayQuestion?.option_2 || question.option_2}
                    </span>
                  </div>
                </button>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={!selectedAnswer || submitting || translating}
                className="w-full py-6 text-base font-semibold rounded-xl"
                data-testid="submit-answer-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('dailyQuestion.checking')}
                  </>
                ) : (
                  t('dailyQuestion.submitAnswer')
                )}
              </Button>
            </>
          ) : (
            /* Result View */
            <div className="text-center py-4">
              {/* Result Icon & Message */}
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
                result.is_correct 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-red-100 text-red-600'
              }`}>
                {result.is_correct ? (
                  <CheckCircle className="h-12 w-12" />
                ) : (
                  <XCircle className="h-12 w-12" />
                )}
              </div>

              <h3 className={`text-2xl font-bold mb-2 ${
                result.is_correct ? 'text-green-600' : 'text-red-600'
              }`}>
                {result.is_correct ? t('dailyQuestion.correct') : t('dailyQuestion.incorrect')}
              </h3>

              {/* Correct Answer Indicator */}
              <div className="mb-4">
                <span className="text-sm text-muted-foreground">
                  {t('dailyQuestion.correctAnswer')}: {t('common.option')} {result.correct_answer === 1 ? 'A' : 'B'}
                </span>
                <p className="font-medium mt-1">
                  {result.correct_answer === 1 
                    ? (displayQuestion?.option_1 || question.option_1) 
                    : (displayQuestion?.option_2 || question.option_2)}
                </p>
              </div>

              {/* Explanation */}
              {(displayResult?.explanation || result.explanation) && (
                <div className="bg-muted/50 rounded-xl p-4 text-left mt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    {t('dailyQuestion.explanation')}:
                  </p>
                  <p className="text-sm leading-relaxed">
                    {displayResult?.explanation || result.explanation}
                  </p>
                </div>
              )}

              {/* Continue Button */}
              <Button
                onClick={handleClose}
                className="w-full mt-6 py-6 text-base font-semibold rounded-xl"
                data-testid="continue-btn"
              >
                {t('dailyQuestion.continueToDashboard')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
